import {
  ref,
  computed,
  watch,
  nextTick,
  type Ref,
  onBeforeUnmount,
  onMounted,
} from 'virtual-vue';

declare const lite: {
  addEventListener?: (_type: string, _listener: () => void) => void;
  removeEventListener?: (_type: string, _listener: () => void) => void;
};

export interface UseNavBarSideWidthOptions {
  leftRef: Ref<HTMLElement | null>;
  rightRef: Ref<HTMLElement | null>;
  /** 侧宽依赖变化时重新量测（如 showLeft / showRight） */
  deps: () => unknown;
}

/**
 * 量测导航栏左/右自然宽度，取 max 后写回两侧，保证标题几何居中（对齐 WeUI）。
 * LiteApp 不支持 requestAnimationFrame，调度统一用 nextTick。
 */
export function useNavBarSideWidth(options: UseNavBarSideWidthOptions) {
  const { leftRef, rightRef } = options;
  /** 两侧统一宽度（px）；0 表示尚未量测，走内容自然宽 */
  const sideWidthPx = ref(0);

  const sideStyle = computed(() => {
    if (!sideWidthPx.value) return undefined;
    return { width: `${sideWidthPx.value}px` };
  });

  function syncSideWidth() {
    const leftEl = leftRef.value;
    const rightEl = rightRef.value;
    if (!leftEl || !rightEl) return;

    const prevWidth = sideWidthPx.value;
    // 先放开内联 width，量自然宽后再写回
    sideWidthPx.value = 0;
    nextTick(() => {
      const width = Math.max(leftEl.offsetWidth, rightEl.offsetWidth);
      sideWidthPx.value = width > 0 ? width : prevWidth;
    });
  }

  watch(options.deps, syncSideWidth);

  onMounted(() => {
    syncSideWidth();
    if (typeof lite !== 'undefined' && typeof lite.addEventListener === 'function') {
      lite.addEventListener('resize', syncSideWidth);
    }
  });

  onBeforeUnmount(() => {
    if (typeof lite !== 'undefined' && typeof lite.removeEventListener === 'function') {
      lite.removeEventListener('resize', syncSideWidth);
    }
  });

  return {
    sideStyle,
    syncSideWidth,
  };
}
